import {Directive, ElementRef, Inject, Input, OnDestroy, OnInit, Optional} from '@angular/core';
import {TableComponent} from '@blocks/table/table.component';
import {BlockMutationCollection} from '@blocks/block/block-mutation.type';
import {Point} from '@angular/cdk/drag-drop';
import {BlockStore} from '@blocks/block/block.store';
import {arrayMove, calcDistance, getPointFromMouseEvent} from '@paperlessio/sdk/util/helpers';
import {Subscription} from 'rxjs';
import {DragMode} from '@blocks/table/drag-mode';
import {BLOCKS_CONFIG, BlocksConfig} from '@blocks/blocks.config';
import {BlockChangeDetectionService} from '@blocks/services/block-change-detection/block-change-detection.service';
import {TableService} from '@blocks/table/table.service';
import {filter} from 'rxjs/operators';
import {TranslocoService} from '@ngneat/transloco';

@Directive({
  selector: 'table[movable]',
  exportAs: 'movable'
})
export class TableMovableDirective implements OnInit, OnDestroy {
  dragStart: Point;
  cursorOffset: Point;

  @Input() movable: boolean;

  private subs = new Subscription();

  constructor(
    private elementRef: ElementRef,
    private tc: TableComponent,
    private tableService: TableService,
    private blockChangeDetectionService: BlockChangeDetectionService,
    private translate: TranslocoService,
    @Optional() private blockStore: BlockStore,
    @Optional() @Inject(BLOCKS_CONFIG) private blocksConfig: BlocksConfig) {
  }

  ngOnInit() {
    if (this.blocksConfig?.editable) {
      this.subs.add(this.tableService.mouseMove$.pipe(filter(e => this.dragStart !== null)).subscribe(event => {
        this.handleMousemove(event);
      }));

      this.subs.add(this.tableService.mouseUp$.pipe(filter(e => this.dragStart !== null)).subscribe(event => {
        this.handleMouseup(event);
      }));
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  handleMousemove(event: MouseEvent): void {
    if (this.dragStart == null) {
      return;
    }

    // Calculate distance to drag start.
    this.tc.dragDistance = calcDistance(this.dragStart, getPointFromMouseEvent(event));
    if (this.tc.dragDistance == null) {
      return;
    }

    let axis = '';

    // Activate dragging if the threshhold is met.
    if (this.tc.dragMode === 'col') {
      axis = 'x';
    } else {
      axis = 'y';
    }

    // When mode is row, the x-axis is relevant
    if (Math.abs(this.tc.dragDistance[axis]) > this.tc.dragThreshhold) {
      this.tc.dragging = true;

      // Get nearest dropLine
      const dropLine = (axis === 'x' ? this.tc.dropCols : this.tc.dropRows)
        .map((dc, index) => {
          return {coords: Math.abs(dc - (this.tc.dragStartOffset[axis] + this.cursorOffset[axis] + this.tc.dragDistance[axis])), index};
        })
        .sort((a, b) => a.coords - b.coords)[0].index;

      if (axis === 'x') {
        this.tc.nearestDropColIndex = dropLine;
      } else {
        this.tc.nearestDropRowIndex = dropLine;
      }
    }

    // By now all properties have been set and we can trigger change detection.
    if (this.tc.dragging) {
      this.tc.cdr.markForCheck();
    }
  }

  handleMouseup(event: MouseEvent): void {
    if (this.tc.dragging) {
      if (this.tc.dragMode === 'col') {
        this.moveCol(this.tc.dragStartIndex, this.tc.nearestDropColIndex);
      } else {
        this.moveRow(this.tc.dragStartIndex, this.tc.nearestDropRowIndex);
      }
    }

    this.tc.cellSelection = null;
    this.tc.dragMode = null;
    this.tc.dragging = false;
    this.dragStart = null;
    this.tc.dragStartIndex = null;
    this.tc.dragDistance = null;
    this.tc.nearestDropColIndex = null;
    this.tc.nearestDropRowIndex = null;
  }

  /**
   * Start the dragging.
   */
  interactionElementMouseDown(rowIndex: number, colIndex: number, event: MouseEvent, mode: DragMode) {
    if (!this.blocksConfig?.editable) {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    const dropLinesCol = this.tc.calculateDropLines('col');
    const dropLinesRow = this.tc.calculateDropLines('row');

    if (mode === 'col') {
      this.tc.dropCols = dropLinesCol;
      // Get the dimensions for the drag shadow.
      this.tc.dragTargetOffset = dropLinesCol[colIndex + 1] - dropLinesCol[colIndex];
    } else {
      this.tc.dropRows = dropLinesRow;
      // Get the dimensions for the drag shadow.
      this.tc.dragTargetOffset = dropLinesRow[colIndex + 1] - dropLinesRow[colIndex];
    }

    this.tc.dragStartIndex = mode === 'row' ? rowIndex : colIndex;

    const targetParent = ((event.target as HTMLElement).parentNode as HTMLElement);

    // dragStartOffset is used to position the shadow correctly behind the cursor.
    this.tc.dragStartOffset = {
      x: targetParent.offsetLeft,
      y: targetParent.offsetTop
    };

    // dragStart is used to calculate the total drag distance and to check for the drag threshhold.
    this.dragStart = getPointFromMouseEvent(event);

    // The mode is defined by the interactionElement that the user positioned the cursor on. Row or Col.
    this.tc.dragMode = mode;

    this.cursorOffset = {
      x: event.offsetX,
      y: event.offsetY
    };
  }

  /**
   * Move a column to a new index.
   */
  moveCol(fromIndex: number, toIndex: number) {
    if (!this.blocksConfig?.editable) {
      return;
    }

    // Only perform operation if the column will actually move
    if ((toIndex - fromIndex) > 1 || (toIndex - fromIndex) < 0) {
      const collection = new BlockMutationCollection(this.tc.block.bucket_type, this.tc.block.bucket_id);

      const correctedToIndex = toIndex > fromIndex ? toIndex - 1 : toIndex;
      this.tc.block.settings.colWidths = arrayMove(this.tc.block.settings.colWidths, fromIndex, correctedToIndex);
      collection.updateBlock(this.tc.block);

      this.tc.children.forEach(row => {
        const cell = row.children[fromIndex];
        collection.moveBlock(cell, toIndex, row);
      });

      this.subs.add(this.blockStore.sendAndUpdate(collection).subscribe(data => {
        this.blockChangeDetectionService.setChangedBlockIds(data.updated_blocks.map(b => b.id));
      }));
    }
  }

  /**
   * Move a row to a new index.
   */
  moveRow(fromIndex: number, toIndex: number) {
    if (!this.blocksConfig?.editable) {
      return;
    }

    if ((toIndex - fromIndex) > 1 || (toIndex - fromIndex) < 0) {
      const collection = new BlockMutationCollection(this.tc.block.bucket_type, this.tc.block.bucket_id);

      const row = this.tc.children[fromIndex];
      collection.moveBlock(row, toIndex, this.tc.block);
      this.subs.add(this.blockStore.sendAndUpdate(collection).subscribe(data => {
        this.blockChangeDetectionService.setChangedBlockIds(data.updated_blocks.map(b => b.id));
      }));
    }
  }
}
